// File: /opt/stacks/workout/app/src/components/workout/ConcentricProgress.js

import React from "react";

import { getPhaseMotionTokens } from "../../utils/planVisuals";
import { cn } from "../../lib/utils";

// Map semantic types to Tailwind CSS stroke colour classes
const colorTypeMap = {
  work: "stroke-work",
  rest: "stroke-rest",
  group: "stroke-group",
  warm: "stroke-warm",
  cool: "stroke-cool",
  workout: "stroke-workout",
  countin: "stroke-countin",
  // Neutral grey used for low-emphasis countdowns (e.g. inter-block setup
  // transitions, where the ring is logistical rather than focal).
  muted: "stroke-white/40",
};

// Map types to glow filter classes from tailwind.config.js
const glowTypeMap = {
  work: "drop-shadow-glow-work",
  rest: "drop-shadow-glow-rest",
  group: "drop-shadow-glow-group",
  warm: "drop-shadow-glow-warm",
  cool: "drop-shadow-glow-cool",
  workout: "drop-shadow-glow-workout",
  countin: "drop-shadow-glow-countin",
  muted: "",
};

// Angle gap between segments in degrees
const SEGMENT_GAP_DEG = 4;
const SEGMENT_TRACK_STROKE = "rgba(255, 255, 255, 0.15)";

export const MIN_SEGMENT_ARC_DEG = 2;

const normalizeRingType = (type) => {
  const normalized = String(type || "").toLowerCase();

  if (normalized.includes("count")) return "countin";
  if (normalized.includes("warm")) return "warm";
  if (normalized.includes("cool")) return "cool";
  if (normalized.includes("rest")) return "rest";
  if (normalized.includes("group")) return "group";
  if (normalized.includes("workout")) return "workout";
  if (normalized === "muted") return "muted";

  return "work";
};

const getColorClass = (type) =>
  colorTypeMap[normalizeRingType(type)] || "stroke-gray-400";

const getGlowClass = (type) => glowTypeMap[normalizeRingType(type)] || "";

export const getSegmentVisualState = ({
  isActive,
  isCompleted,
  invert = false,
}) => {
  if (isActive) return "active";
  return invert
    ? isCompleted
      ? "empty"
      : "filled"
    : isCompleted
      ? "filled"
      : "empty";
};

const getSegmentClassName = (type, visualState) => {
  const colorClass = getColorClass(type);
  const glowClass = getGlowClass(type);

  if (visualState === "active") {
    return `${colorClass} ${glowClass} opacity-100 animate-segment-pulse transition-[stroke-dasharray,stroke-dashoffset,opacity]`;
  }

  if (visualState === "filled") {
    return `${colorClass} opacity-30 transition-opacity`;
  }

  return "";
};

const getSegmentMotionStyle = (type, visualState) => {
  const motionTokens = getPhaseMotionTokens(type);

  if (visualState === "active") {
    return {
      animationDuration: `${motionTokens.segmentPulseMs}ms`,
      transitionDuration: `${motionTokens.progressTransitionMs}ms`,
    };
  }

  if (visualState === "filled") {
    return {
      transitionDuration: `${motionTokens.progressTransitionMs}ms`,
    };
  }

  return undefined;
};

const getContinuousRingMotionStyle = (type, isIndeterminate) => {
  const motionTokens = getPhaseMotionTokens(type);

  return {
    transitionDuration: `${motionTokens.progressTransitionMs}ms`,
    ...(isIndeterminate
      ? { animationDuration: `${motionTokens.indeterminatePulseMs}ms` }
      : {}),
  };
};

const getVisibleSegmentLength = ({
  segmentLen,
  visualState,
  progress = 0,
  invert = false,
  discrete = false,
}) => {
  if (visualState === "empty") return 0;
  if (visualState === "filled" || discrete) return segmentLen;

  const clampedProgress = Math.max(0, Math.min(1, progress));
  const activeRatio = invert ? 1 - clampedProgress : clampedProgress;
  if (activeRatio <= 0) return 0;

  return Math.max(0.01, segmentLen * activeRatio);
};

const renderSegmentArc = ({
  keyPrefix,
  center,
  radius,
  strokeWidth,
  circumference,
  segmentLen,
  adjustedOffset,
  type,
  visualState,
  progress = 0,
  invert = false,
  discrete = false,
}) => {
  const arcs = [
    <circle
      key={`${keyPrefix}-bg`}
      cx={center}
      cy={center}
      r={radius}
      fill="none"
      stroke={SEGMENT_TRACK_STROKE}
      strokeWidth={strokeWidth}
      strokeDasharray={`${segmentLen} ${circumference - segmentLen}`}
      strokeDashoffset={adjustedOffset}
      strokeLinecap="butt"
    />,
  ];

  const visibleLen = getVisibleSegmentLength({
    segmentLen,
    visualState,
    progress,
    invert,
    discrete,
  });

  if (visibleLen <= 0) return arcs;

  arcs.push(
    <circle
      key={`${keyPrefix}-fg`}
      className={getSegmentClassName(type, visualState)}
      style={getSegmentMotionStyle(type, visualState)}
      cx={center}
      cy={center}
      r={radius}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeDasharray={`${visibleLen} ${circumference - visibleLen}`}
      strokeDashoffset={adjustedOffset}
      strokeLinecap="butt"
    />,
  );

  return arcs;
};

export const distributeSegmentRadians = (
  segments,
  gapDeg = SEGMENT_GAP_DEG,
  minArcDeg = MIN_SEGMENT_ARC_DEG,
) => {
  if (!segments || segments.length < 1) return null;

  const totalDuration = segments.reduce(
    (sum, segment) => sum + Math.max(0, segment.duration || 0),
    0,
  );
  if (totalDuration <= 0) return null;

  const gapRad = (gapDeg * Math.PI) / 180;
  const totalGapRad = gapRad * segments.length;
  const availableRad = 2 * Math.PI - totalGapRad;
  if (availableRad <= 0) return null;

  const minArcRad = Math.min(
    (minArcDeg * Math.PI) / 180,
    availableRad / segments.length,
  );

  let arcRadians = segments.map(
    (segment) =>
      (Math.max(0, segment.duration || 0) / totalDuration) * availableRad,
  );

  let deficit = 0;
  arcRadians = arcRadians.map((arc) => {
    if (arc < minArcRad) {
      deficit += minArcRad - arc;
      return minArcRad;
    }
    return arc;
  });

  if (deficit > 0) {
    const adjustableTotal = arcRadians.reduce(
      (sum, arc) => (arc > minArcRad ? sum + (arc - minArcRad) : sum),
      0,
    );

    if (adjustableTotal > 0) {
      arcRadians = arcRadians.map((arc) =>
        arc > minArcRad
          ? arc - (deficit * (arc - minArcRad)) / adjustableTotal
          : arc,
      );
    }
  }

  return { gapRad, arcRadians };
};

/**
 * Render a single continuous-progress ring.
 * Supports growing (elapsed) or shrinking (countdown) animations.
 */
const ContinuousRing = ({
  item,
  center,
  radius,
  strokeWidth,
  invert = false,
}) => {
  if (radius <= 0) return null;

  const circumference = 2 * Math.PI * radius;
  const activeCurrent = item.discrete ? Math.floor(item.current) : item.current;

  // Override progress to 100% if indeterminate
  const isIndeterminate = item.indeterminate;
  let progress = isIndeterminate
    ? 1
    : Math.max(0, Math.min(1, item.total > 0 ? activeCurrent / item.total : 0));

  // Drain mode: flip progress so the ring starts full and empties
  if (invert && !isIndeterminate) {
    progress = 1 - progress;
  }

  const offset = item.countdown
    ? -(circumference * (1 - progress))
    : circumference * (1 - progress);

  const colorClass = getColorClass(item.type);
  const glowClass = getGlowClass(item.type);
  const indeterminateClass = isIndeterminate ? "animate-slow-pulse" : "";

  return (
    <>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="rgba(255, 255, 255, 0.1)"
        strokeWidth={strokeWidth}
      />
      <circle
        className={`${colorClass} ${glowClass} ${indeterminateClass} transition-[stroke-dashoffset,stroke]`}
        style={getContinuousRingMotionStyle(item.type, isIndeterminate)}
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
      />
    </>
  );
};

/**
 * Render a segmented ring where each segment is a discrete chunk.
 * Uses square ends ("butt" linecaps) for crisp differentiation of small gaps.
 */
const SegmentedRing = ({
  item,
  center,
  radius,
  strokeWidth,
  invert = false,
}) => {
  const segmentCount = item.segmentItems?.length || item.segments;
  if (radius <= 0 || !segmentCount || segmentCount < 1) return null;

  const {
    completed = 0,
    active,
    currentProgress = 0,
    type,
    discrete = false,
  } = item;
  const circumference = 2 * Math.PI * radius;

  // Total gap angle in radians
  const gapRad = (SEGMENT_GAP_DEG * Math.PI) / 180;
  const totalGapRad = gapRad * segmentCount;
  const availableRad = 2 * Math.PI - totalGapRad;
  const segmentRad = availableRad / segmentCount;

  // Convert radians directly to arc length (no cap compensations needed for 'butt')
  const segmentLen = segmentRad * radius;
  const drawLen = Math.max(0.01, segmentLen);

  const arcs = [];
  const completedCount = Math.max(0, Math.min(segmentCount, completed));
  const activeIndex =
    active != null
      ? active
      : completedCount < segmentCount
        ? completedCount
        : undefined;

  for (let i = 0; i < segmentCount; i++) {
    const startRad = i * (segmentRad + gapRad);
    const adjustedOffset = -(startRad * radius);

    const isCompleted = i < completedCount;
    const isActive = activeIndex != null && i === activeIndex;
    const visualState = getSegmentVisualState({
      isActive,
      isCompleted,
      invert,
    });
    const segmentType = item.segmentItems?.[i]?.type || type;

    arcs.push(
      ...renderSegmentArc({
        keyPrefix: `seg-${i}`,
        center,
        radius,
        strokeWidth,
        circumference,
        segmentLen: drawLen,
        adjustedOffset,
        type: segmentType,
        visualState,
        progress: isActive ? currentProgress : isCompleted ? 1 : 0,
        invert,
        discrete,
      }),
    );
  }

  return <>{arcs}</>;
};

/**
 * Render a proportional segmented ring using square ends.
 */
const ProportionalSegmentedRing = ({
  segments,
  center,
  radius,
  strokeWidth,
  invert = false,
}) => {
  if (radius <= 0 || !segments || segments.length < 1) return null;

  const circumference = 2 * Math.PI * radius;
  const distribution = distributeSegmentRadians(segments);
  if (!distribution) return null;

  const { gapRad, arcRadians } = distribution;

  const arcs = [];
  let currentRad = 0;

  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    const segmentRad = arcRadians[i];
    const segmentLen = segmentRad * radius;
    const drawLen = Math.max(0.01, segmentLen);

    const startRad = currentRad;
    const adjustedOffset = -(startRad * radius);

    arcs.push(
      ...renderSegmentArc({
        keyPrefix: `pseg-${i}`,
        center,
        radius,
        strokeWidth,
        circumference,
        segmentLen: drawLen,
        adjustedOffset,
        type: seg.type,
        visualState: getSegmentVisualState({
          isActive: !!seg.active,
          isCompleted: !!seg.completed,
          invert,
        }),
        progress: seg.active ? seg.progress : seg.completed ? 1 : 0,
        invert,
        discrete: false,
      }),
    );

    currentRad += segmentRad + gapRad;
  }

  return <>{arcs}</>;
};

// Free training's dial (#157): what you logged, in the Coach's work colour
// whatever its kind: blue reads as rest and amber as warm-up everywhere else
// in the Coach. Rests you mark are in the rest colour. The open arc is the
// time since your last entry.
const TIMELINE_KIND_CLASS = {
  lift: "text-work",
  body: "text-work",
  cardio: "text-work",
  block: "text-work",
  // Marked with the Rest button: the Coach's rest colour.
  rest: "text-rest",
  now: "text-white/30",
};
const TIMELINE_GAP_RAD = 0.006 * 2 * Math.PI;

/**
 * A ring that's a clock face: `span` seconds all the way round, and each arc
 * a stretch of the session ({ from, to, kind } in seconds). `now` draws the
 * open arc from the last stretch to the present.
 */
const TimelineRing = ({ item, center, radius, strokeWidth }) => {
  if (radius <= 0) return null;
  const circumference = 2 * Math.PI * radius;
  const span = Math.max(1, item.span || 3600);
  const arcs = [...(item.arcs || [])];
  const end = arcs.length ? arcs[arcs.length - 1].to : 0;
  if (item.now > end) arcs.push({ from: end, to: item.now, kind: "now" });
  return (
    <>
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        stroke={SEGMENT_TRACK_STROKE}
        strokeWidth={strokeWidth}
      />
      {arcs.map((arc, i) => {
        const from = Math.min(arc.from, span);
        const to = Math.min(arc.to, span);
        const len = Math.max(0, ((to - from) / span) * circumference - TIMELINE_GAP_RAD * radius);
        if (len <= 0) return null;
        return (
          <circle
            key={`tl-${i}`}
            className={cn(TIMELINE_KIND_CLASS[arc.kind] || TIMELINE_KIND_CLASS.lift, "transition-[stroke-dasharray]")}
            style={arc.kind === "now" ? undefined : { filter: "drop-shadow(0 0 4px currentColor)" }}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            strokeDasharray={`${len} ${circumference}`}
            strokeDashoffset={-(from / span) * circumference}
            strokeLinecap="butt"
          />
        );
      })}
    </>
  );
};

const ConcentricProgress = ({
  items = [],
  size = 300,
  strokeWidth = 12,
  spinnerMode = false,
  paused = false,
  drainTimer = false,
  drainSegments = false,
}) => {
  const center = size / 2;
  const initialRadius = center - strokeWidth / 2;
  const gap = 6; // Gap between rings

  // Support up to 3 rings
  const displayItems = items.slice(0, 3);

  // Spinner mode: rotating arc for stopwatch/count-up timers
  const showSpinner = spinnerMode && displayItems.length === 0;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="transform -rotate-90 overflow-visible"
    >
      {showSpinner ? (
        <>
          <circle
            cx={center}
            cy={center}
            r={initialRadius}
            fill="none"
            stroke="rgba(255, 255, 255, 0.1)"
            strokeWidth={strokeWidth}
          />
          <circle
            className="stroke-work drop-shadow-glow-work"
            cx={center}
            cy={center}
            r={initialRadius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeDasharray={`${2 * Math.PI * initialRadius * 0.15} ${2 * Math.PI * initialRadius * 0.85}`}
            strokeLinecap="round"
            style={{
              transformOrigin: `${center}px ${center}px`,
              animation: "spin 4s linear infinite",
              animationPlayState: paused ? "paused" : "running",
              opacity: 0.8,
            }}
          />
        </>
      ) : (
        displayItems.map((item, index) => {
          const radius = initialRadius - index * (strokeWidth + gap);

          if ("timeline" in item) {
            return (
              <TimelineRing
                key={`ring-${index}`}
                item={item.timeline}
                center={center}
                radius={radius}
                strokeWidth={strokeWidth}
              />
            );
          }

          if ("proportionalSegments" in item) {
            return (
              <ProportionalSegmentedRing
                key={`ring-${index}`}
                segments={item.proportionalSegments}
                center={center}
                radius={radius}
                strokeWidth={strokeWidth}
                invert={drainSegments}
              />
            );
          }

          if ("segments" in item) {
            return (
              <SegmentedRing
                key={`ring-${index}`}
                item={item}
                center={center}
                radius={radius}
                strokeWidth={strokeWidth}
                invert={drainSegments}
              />
            );
          }

          return (
            <ContinuousRing
              key={`ring-${index}`}
              item={item}
              center={center}
              radius={radius}
              strokeWidth={strokeWidth}
              invert={drainTimer}
            />
          );
        })
      )}
    </svg>
  );
};

export default ConcentricProgress;
